// =========================
// API SERVICE
// =========================
// Use local backend during development; change to production URL in deployment
const API_BASE_URL = 'https://greenyellow-nightingale-241896.hostingersite.com';

class ApiService {
  // ===== Helper Request Method =====
  async request(endpoint, options = {}) {
    const url = `${API_BASE_URL}/api${endpoint}`;

    // Get token from localStorage
    const token = localStorage.getItem('authToken');

    // Prepare headers, including Authorization if token exists
    const headers = {
      ...options.headers,
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Determine if we're sending FormData
    const isFormData = (typeof FormData !== 'undefined') && (options.body instanceof FormData);

    // Only set JSON content-type for non-FormData bodies
    if (!isFormData) {
      headers['Content-Type'] = headers['Content-Type'] || 'application/json';
    }

    const config = {
      method: options.method || 'GET',
      headers,
      cache: 'no-store',
      body: options.body ? (isFormData ? options.body : JSON.stringify(options.body)) : undefined,
    };

    try {
      console.log('API request:', { url, method: config.method, isFormData: isFormData, headers });
      const response = await fetch(url, config);
      console.log('API response status:', response.status, response.statusText);

      if (response.status === 204) return null; // No content

      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || `HTTP error! Status: ${response.status}`);
        }
        return data;
      } else {
        const text = await response.text();
        if (!response.ok) {
          throw new Error(text || `HTTP error! Status: ${response.status}`);
        }
        return text;
      }
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }



  // ======================== PROJECTS ========================
  getProjects() {
    return this.request('/projectsmgnt');
  }


  getProject(id) {
    return this.request(`/projectsmgnt/${id}`);
  }

  createProject(projectData) {
    return this.request('/projectsmgnt', { method: 'POST', body: projectData });
  }

  updateProject(id, projectData) {
    return this.request(`/projectsmgnt/${id}`, { method: 'PUT', body: projectData });
  }

  deleteProject(id) {
    return this.request(`/projectsmgnt/${id}`, { method: 'DELETE' });
  }

  // ======================== EMPLOYEES ========================
  async getEmployees() {
    // Get company ID from JWT token to fetch only company employees
    const token = localStorage.getItem('authToken');
    if (token) {
      try {
        // Decode JWT to get company ID
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        const decodedToken = JSON.parse(jsonPayload);
        console.log("decodedToken", decodedToken);

        // const companyId = decodedToken.companyId ;
        const companyId = decodedToken.role === 'company'
          ? (decodedToken.companyId || decodedToken.id)
          : decodedToken.companyId;

        // Fetch employees by company
        const response = await this.request(`/employees/company/${companyId}`);
        console.log("res", response);

        return Array.isArray(response) ? response : response.data || [];
      } catch (error) {
        console.error('Error decoding token or fetching company employees:', error);
        // Fallback to all employees if company-specific fetch fails
        return this.request('/employees');
      }
    }
    // Fallback if no token
    return this.request('/employees');
  }

  getEmployee(id) {
    return this.request(`/employees/${id}`);
  }

  createEmployee(employeeData) {
    return this.request('/employees', { method: 'POST', body: employeeData });
  }

  updateEmployee(id, employeeData) {
    return this.request(`/employees/${id}`, { method: 'PUT', body: employeeData });
  }

  deleteEmployee(id) {
    return this.request(`/employees/${id}`, { method: 'DELETE' });
  }

  // ======================== MILESTONES ========================
  async getMilestones() {
    // FIXED: Use plural to match backend `/api/milestones`
    const response = await this.request('/milestones');
    // Handle nested response structure: {milestones: Array} or direct Array
    return response.milestones || response;
  }

  getMilestone(id) {
    return this.request(`/milestones/${id}`);
  }

  createMilestone(milestoneData) {
    return this.request('/milestones', { method: 'POST', body: milestoneData });
  }

  updateMilestone(id, milestoneData) {
    return this.request(`/milestones/${id}`, { method: 'PUT', body: milestoneData });
  }

  deleteMilestone(id) {
    return this.request(`/milestones/${id}`, { method: 'DELETE' });
  }

  // Alternative: Update milestone and upload files together via multipart
  updateMilestoneWithFiles(id, fields = {}, files = []) {
    const fd = new FormData();
    fd.append('milestoneId', id);
    Object.entries(fields || {}).forEach(([k, v]) => {
      // If value is an object or array, stringify it so backend can parse
      if (v !== null && (Array.isArray(v) || typeof v === 'object')) {
        try {
          fd.append(k, JSON.stringify(v));
        } catch (e) {
          fd.append(k, String(v ?? ''));
        }
      } else {
        fd.append(k, v ?? '');
      }
    });
    // Append files using server-expected field names only to avoid Multer 'Unexpected field'
    (files || []).forEach((f) => {
      fd.append('images', f);
    });
    console.log('updateMilestoneWithFiles - formdata keys:', Array.from(fd.keys()));
    return this.request(`/milestones/${id}`, { method: 'PUT', body: fd });
  }

  // Upload milestone attachments (expects backend support at POST /api/milestones/:id/attachments)
  uploadMilestoneFiles(id, files) {
    const fd = new FormData();
    fd.append('milestoneId', id);
    // Append files using server-expected field names only
    (files || []).forEach((f) => {
      fd.append('images', f);
    });
    return this.request(`/milestones/${id}/attachments`, { method: 'POST', body: fd });
  }

  // Delete milestones by project ID or project name
  async deleteMilestonesByProject(projectId, projectName) {
    try {
      // First, get all milestones
      const milestones = await this.getMilestones();

      // Filter milestones that belong to the project
      const milestonesToDelete = milestones.filter(milestone => {
        // Check if milestone belongs to this project by ID or name
        return (
          (milestone.projectId && milestone.projectId === projectId) ||
          (milestone.projectName && (
            milestone.projectName === projectName ||
            milestone.projectName === projectId // Sometimes projectName might contain the ID
          ))
        );
      });

      console.log(`Found ${milestonesToDelete.length} milestones to delete for project:`, { projectId, projectName });

      // Delete each milestone
      const deletePromises = milestonesToDelete.map(milestone => {
        const milestoneId = milestone._id || milestone.id;
        console.log('Deleting milestone:', milestoneId, milestone.title);
        return this.deleteMilestone(milestoneId);
      });

      // Wait for all deletions to complete
      await Promise.all(deletePromises);

      console.log(`Successfully deleted ${milestonesToDelete.length} milestones for project`);
      return milestonesToDelete.length;
    } catch (error) {
      console.error('Error deleting milestones by project:', error);
      throw error;
    }
  }
}

export default new ApiService();


